import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {AddToBasketTrackingData} from '@aboutyou/user';
import React, {useEffect, useState} from 'react';
import {BasketFlyout} from '../../backend-services/Basket/BasketFlyout/BasketFlyout';
import {useBasketFlyoutOpenedState} from '../../backend-services/Basket/hooks/useBasketFlyoutOpenedState';
import {
  MiniAdpDialogOpenedStateKey,
  useMiniAdpDialogOpenedState,
} from '../MiniADPDialog/useMiniAdpDialogOpenedState';
import {
  ProductMiniAdp,
  ProductMiniAdpProps,
} from '../ProductMiniAdp/ProductMiniAdp';

interface ProductMiniAdpWithBasketFlyoutProps
  extends Pick<
    ProductMiniAdpProps,
    'showAddToWishlistButton' | 'sizeSelectionStateManagerKey'
  > {
  freeDeliverySection?: FreeDeliverySection;
  statusSuccessSections?: AddItemResponseStatus_Success_Section[];
  renderInPortal?: boolean;
  onSuccess?: VoidFunction;
  miniADPModalStateManagerKey?: MiniAdpDialogOpenedStateKey;
  basketFlyoutStateManagerKey?: string;
  recoType?: string;
  addToBasketTrackingData?: AddToBasketTrackingData;
}

export const ProductMiniAdpWithBasketFlyout: React.FC<
  ProductMiniAdpWithBasketFlyoutProps
> = ({
  showAddToWishlistButton,
  sizeSelectionStateManagerKey,
  freeDeliverySection,
  statusSuccessSections,
  renderInPortal,
  onSuccess,
  miniADPModalStateManagerKey,
  basketFlyoutStateManagerKey,
  recoType,
  addToBasketTrackingData,
}) => {
  const {
    close,
    filteredSizeId,
    isOpen,
    productId,
    selectedSize,
    setSelectedSize,
    couponPromotionId,
    recoType: dialogRecoType,
    streamPosition,
    locationId,
  } = useMiniAdpDialogOpenedState(miniADPModalStateManagerKey);

  const {open: openBasketFlyout} = useBasketFlyoutOpenedState(
    basketFlyoutStateManagerKey,
  );

  const [freeDeliverySectionData, setFreeDeliverySectionData] = useState<
    FreeDeliverySection | undefined
  >();

  useEffect(() => {
    // The freeDeliverySection data on the ADP comes from the AddToBasket button rather than the ADP quick view.
    // Therefore, it's passed in as a prop and should be reflected here.
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setFreeDeliverySectionData(freeDeliverySection);
  }, [freeDeliverySection]);

  const [statusSuccessSectionData, setStatusSuccessSectionData] = useState<
    AddItemResponseStatus_Success_Section[] | undefined
  >();

  useEffect(() => {
    // The statusSuccessSections data on the ADP comes from the AddToBasket button rather than the ADP quick view.
    // Therefore, it's passed in as a prop and should be reflected here.
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    setStatusSuccessSectionData(statusSuccessSections);
  }, [statusSuccessSections]);

  const onAddToBasketSuccess = ({
    productId,
    freeDeliverySection,
    statusSuccessSections,
  }: {
    productId?: number;
    freeDeliverySection?: FreeDeliverySection;
    statusSuccessSections: AddItemResponseStatus_Success_Section[];
  }) => {
    // This is a temporary fix until the root cause will be fixed with https://aboutyou.atlassian.net/browse/AYFEP-2859
    setTimeout(() => openBasketFlyout(productId!), 100);

    setFreeDeliverySectionData(freeDeliverySection);
    setStatusSuccessSectionData(statusSuccessSections);

    onSuccess?.();
  };

  const trackingData =
    recoType || dialogRecoType || locationId
      ? {
          recoType: locationId || recoType || dialogRecoType,
          // add empty list to make force the tracking event send the given reco type
          elements: [],
          streamPosition: streamPosition || 0,
        }
      : undefined;

  return (
    <>
      <ProductMiniAdp
        close={close}
        isOpen={isOpen}
        productId={productId}
        filteredSizeId={filteredSizeId}
        selectedSize={selectedSize}
        setSelectedSize={setSelectedSize}
        sizeSelectionStateManagerKey={sizeSelectionStateManagerKey}
        onSuccess={onAddToBasketSuccess}
        showAddToWishlistButton={showAddToWishlistButton}
        renderInPortal={renderInPortal}
        trackingData={trackingData}
        overrideBETrackingDataState={addToBasketTrackingData}
        couponPromotionId={couponPromotionId}
      />

      <BasketFlyout
        freeDeliverySection={freeDeliverySectionData}
        statusSuccessSections={statusSuccessSectionData ?? []}
        basketFlyoutStateManagerKey={basketFlyoutStateManagerKey}
        miniADPModalStateManagerKey={miniADPModalStateManagerKey}
      />
    </>
  );
};
